import {
  AUTH_URL,
  INVOICE_REQUEST_URL,
  INVOICE_STATUS_URL,
  STUB,
} from "./config";

export type RemoteInvoiceStatus = "pending" | "confirmed" | "failed";

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Stub mode: confirm after a couple of polls so the demo shows pending -> confirmed.
const stubPollCount = new Map<string, number>();

// Stub mode: mimic the backend's 5-requests/hour limit so the 429 UI is demoable
// without the real backend. The 6th request returns 429. Resets on page reload.
const STUB_INVOICE_LIMIT = 5;
let stubInvoiceCount = 0;

// POST /auth — authenticate the client from the LoginForm.
export async function authenticate(
  username: string,
  password: string,
): Promise<{ clientId: string }> {
  if (STUB) {
    await delay(600);
    if (!username || !password) throw new Error("Enter your Base ID and password.");
    return { clientId: `client_${username}` };
  }
  const res = await fetch(AUTH_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) throw new Error(`Sign in failed (${res.status})`);
  return (await res.json()) as { clientId: string };
}

// POST /invoice/request — texts a payment link, returns an id to poll.
export async function requestInvoice(
  clientId: string | null,
): Promise<{ invoiceId: string }> {
  if (STUB) {
    await delay(500);
    stubInvoiceCount += 1;
    if (stubInvoiceCount > STUB_INVOICE_LIMIT) {
      const error = new Error("Too many requests") as Error & { status?: number };
      error.status = 429;
      throw error;
    }
    return { invoiceId: `inv_${Date.now()}` };
  }
  const res = await fetch(INVOICE_REQUEST_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ clientId }),
  });
  if (!res.ok) {
    const error = new Error(`Could not send invoice (${res.status})`) as Error & {
      status?: number;
    };
    error.status = res.status; // lets the UI special-case 429 (rate limited)
    throw error;
  }
  return (await res.json()) as { invoiceId: string };
}

// GET /invoice/status — polled until the payment resolves.
export async function getInvoiceStatus(
  invoiceId: string,
): Promise<{ status: RemoteInvoiceStatus }> {
  if (STUB) {
    const n = (stubPollCount.get(invoiceId) ?? 0) + 1;
    stubPollCount.set(invoiceId, n);
    return { status: n >= 2 ? "confirmed" : "pending" };
  }
  const url = `${INVOICE_STATUS_URL}?invoiceId=${encodeURIComponent(invoiceId)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Status check failed (${res.status})`);
  return (await res.json()) as { status: RemoteInvoiceStatus };
}
